// frontend/src/utils/remoteLog.ts
/**
 * Remote-log transport: batches structured client-log entries and ships them to
 * the backend `POST /api/client-logs` ingest (ADR-009) so frontend failures join
 * the backend stream in one Cloud Logging view.
 *
 * Design (ADR-009):
 * - In-memory queue with batching, sampling, and a size cap (drop oldest on
 *   overflow) so a render-loop can't flood the backend or balloon cost.
 * - Keep ALL `ERROR`; sample `INFO`/`WARNING` at a fixed fraction.
 * - Flush on a timer when the queue is non-empty, and on `beforeunload` /
 *   `visibilitychange` (hidden) via `navigator.sendBeacon`, falling back to
 *   `fetch(..., { keepalive: true })`.
 * - The endpoint is authenticated by IAP like the rest of the API.
 * - HIPAA: never put PHI in `message`/`stack` deliberately; the backend performs
 *   authoritative scrubbing as the backstop.
 * - Self-recursion guard: a failed POST to the ingest endpoint must NOT enqueue
 *   another client-log, or a backend outage would loop forever.
 *
 * Reads `import.meta.env` directly (never `process.env` — Vite browser build) and
 * avoids importing the `config` module so this transport stays free of config's
 * import-time side effects.
 */

/** Severity levels accepted by the client-log ingest contract. */
export type ClientLogSeverity = "ERROR" | "WARNING" | "INFO";

/** A single structured client-log entry, matching the pinned backend contract. */
export interface ClientLogEntry {
  /** Log severity. */
  severity: ClientLogSeverity;
  /** Human-readable message (no PHI). */
  message: string;
  /** Optional error class/type name. */
  error_type?: string | null;
  /** Optional stack trace (no PHI). */
  stack?: string | null;
  /** Originating route path, query string stripped. */
  route?: string | null;
  /** Build version (git commit hash baked at build time). */
  build_version?: string | null;
  /** Session/correlation id, correlated with backend request logs. */
  correlation_id?: string | null;
  /** ISO-8601 timestamp. */
  ts: string;
}

/** Fields a caller supplies; transport fills in the rest. */
export interface ClientLogInput {
  /** Log severity. */
  severity: ClientLogSeverity;
  /** Human-readable message (no PHI). */
  message: string;
  /** Optional error class/type name. */
  errorType?: string | null;
  /** Optional stack trace (no PHI). */
  stack?: string | null;
  /**
   * Bypass INFO/WARNING sampling (BLA-348). Latency-metric entries (see
   * `utils/perfMetrics.ts`) must never be dropped by the fixed-fraction
   * sample, or a percentile computed from the shipped subset would be biased
   * — unlike an ordinary INFO log, where dropping 90% is an acceptable cost
   * trade-off because no one is aggregating percentiles over it.
   */
  alwaysKeep?: boolean;
}

/** Path of the ingest endpoint (relative to the backend base URL). */
const INGEST_PATH = "/api/client-logs";

/** Max entries retained in the queue before dropping the oldest. */
const MAX_QUEUE_SIZE = 50;

/** Flush interval in milliseconds (the timer flushes only when non-empty). */
const FLUSH_INTERVAL_MS = 5000;

/** Fraction of INFO/WARNING entries kept (ERROR is always kept). */
const SAMPLE_RATE = 0.1;

/** In-memory queue of pending entries. */
const queue: ClientLogEntry[] = [];

/** Session/correlation id, set by apiUtils once the session is established. */
let correlationId: string | null = null;

/** The active flush timer, if started. */
let flushTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Register the session/correlation id so shipped entries correlate with backend
 * request logs.
 *
 * Args:
 *   id: The stable per-session correlation id (from apiUtils).
 *
 * Returns:
 *   None.
 */
export function setCorrelationId(id: string): void {
  correlationId = id;
}

/**
 * Resolve the current route path with the query string stripped (drops
 * `patient_id` and other PHI-bearing params).
 *
 * Returns:
 *   The pathname, or null when `window` is unavailable.
 */
function currentRoute(): string | null {
  if (typeof window === "undefined" || !window.location) {
    return null;
  }
  return window.location.pathname;
}

/**
 * Decide whether an entry of the given severity should be kept.
 *
 * Args:
 *   severity: The entry severity.
 *
 * Returns:
 *   True to keep the entry; ERROR is always kept, INFO/WARNING are sampled.
 */
function shouldKeep(
  severity: ClientLogSeverity,
  alwaysKeep?: boolean,
): boolean {
  if (severity === "ERROR" || alwaysKeep) {
    return true;
  }
  return Math.random() < SAMPLE_RATE;
}

/**
 * Enqueue a client-log entry for shipment (subject to sampling + size cap).
 *
 * Args:
 *   input: The caller-supplied severity, message, and optional error metadata.
 *
 * Returns:
 *   None.
 */
export function enqueue(input: ClientLogInput): void {
  if (!shouldKeep(input.severity, input.alwaysKeep)) {
    return;
  }

  const entry: ClientLogEntry = {
    severity: input.severity,
    message: input.message,
    error_type: input.errorType ?? null,
    stack: input.stack ?? null,
    route: currentRoute(),
    build_version: import.meta.env.VITE_COMMIT_HASH ?? null,
    correlation_id: correlationId,
    ts: new Date().toISOString(),
  };

  queue.push(entry);
  // Cap the queue; drop the oldest on overflow so a loop can't grow unbounded.
  while (queue.length > MAX_QUEUE_SIZE) {
    queue.shift();
  }
}

/**
 * Build the ingest request body from a batch of entries.
 *
 * Args:
 *   entries: The drained batch.
 *
 * Returns:
 *   A JSON string matching the `{ entries: [...] }` contract.
 */
function serialize(entries: ClientLogEntry[]): string {
  return JSON.stringify({ entries });
}

/**
 * Resolve the absolute ingest URL from the backend base.
 *
 * Returns:
 *   The full URL to POST client logs to.
 */
function ingestUrl(): string {
  return `${import.meta.env.VITE_BACKEND_URL ?? ""}${INGEST_PATH}`;
}

/**
 * Ship a batch via `navigator.sendBeacon`, falling back to keepalive `fetch`.
 *
 * The self-recursion guard lives at the call sites that wrap fetch failures
 * (apiUtils): a failed POST here never re-enqueues a client-log, because this
 * module never calls `enqueue` on its own failures.
 *
 * Args:
 *   entries: The batch to ship.
 *
 * Returns:
 *   None.
 */
function ship(entries: ClientLogEntry[]): void {
  const body = serialize(entries);
  const url = ingestUrl();

  if (
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    const blob = new Blob([body], { type: "application/json" });
    const queued = navigator.sendBeacon(url, blob);
    if (queued) {
      return;
    }
  }

  // Fallback: keepalive fetch survives page teardown. Swallow failures — we must
  // never enqueue a client-log about a failed client-log POST (infinite loop).
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    // Intentionally ignored: do not re-enqueue (self-recursion guard).
  });
}

/**
 * Drain the queue and ship the batch. No-op when the queue is empty.
 *
 * Returns:
 *   None.
 */
export function flush(): void {
  if (queue.length === 0) {
    return;
  }
  const batch = queue.splice(0, queue.length);
  ship(batch);
}

/**
 * Whether the given URL targets the client-log ingest endpoint. Used by the
 * fetch wrapper to suppress re-enqueueing its own failed ingest POSTs.
 *
 * Args:
 *   url: The request URL to test.
 *
 * Returns:
 *   True when the URL is the ingest endpoint.
 */
export function isIngestUrl(url: string): boolean {
  return url.includes(INGEST_PATH);
}

/**
 * Handle a global `window.error` event by enqueueing an ERROR entry. Catches the
 * `e.trim`-class synchronous crashes that are invisible today.
 *
 * Args:
 *   event: The ErrorEvent dispatched by the runtime.
 *
 * Returns:
 *   None.
 */
export function handleWindowError(event: ErrorEvent): void {
  const err = event.error instanceof Error ? event.error : null;
  enqueue({
    severity: "ERROR",
    message: event.message || "Uncaught error",
    errorType: err?.name ?? null,
    stack: err?.stack ?? null,
  });
}

/**
 * Handle a global `unhandledrejection` event by enqueueing an ERROR entry.
 * Catches async promise rejections that no try/catch saw.
 *
 * Args:
 *   event: The PromiseRejectionEvent dispatched by the runtime.
 *
 * Returns:
 *   None.
 */
export function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  const err = reason instanceof Error ? reason : null;
  enqueue({
    severity: "ERROR",
    message: err?.message ?? `Unhandled rejection: ${String(reason)}`,
    errorType: err?.name ?? "UnhandledRejection",
    stack: err?.stack ?? null,
  });
}

/**
 * Initialize the transport: start the flush timer, register the global error +
 * unhandledrejection handlers, and register lifecycle handlers that flush on
 * page teardown. Idempotent.
 *
 * Returns:
 *   None.
 */
export function initRemoteLog(): void {
  if (flushTimer === null) {
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  }

  if (typeof window !== "undefined") {
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
  }

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    });
  }
}
